import { useFetch } from '@/composables/useFetch';
import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import {
  ISetWorkspaceGroupKksRangeDto,
  IWorkspaceGroupKksRangeDto,
} from 'types';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export function useWorkspaceGroupKksScopes(groupId: string) {
  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();

  const {
    data: workspaceGroupKksRange,
    fetchData: fetchWorkspaceGroupKksRange,
    isLoading: isLoadingWorkspaceGroupKksRange,
  } = useFetch(getWorkspaceGroupKksRange, undefined, false);

  async function getWorkspaceGroupKksRange() {
    try {
      const response = await axios.get<IWorkspaceGroupKksRangeDto>(
        endpointsList.workspaces.getWorkspaceGroupKksRange(groupId),
      );
      return response.data;
    } catch {
      createErrorToast(
        t('workspaceGroupPermissions.errors.errorGettingKksRange'),
      );
    }
  }

  const isSaving = ref(false);
  async function saveWorkspaceGroupKksRange(
    startNode: string,
  ): Promise<boolean> {
    const dto: ISetWorkspaceGroupKksRangeDto = { startNode };
    try {
      isSaving.value = true;
      await axios.post(
        endpointsList.workspaces.upsertWorkspaceGroupKksRange(groupId),
        dto,
      );
      createSuccessToast(t('workspaceGroupPermissions.success.kksRangeSaved'));
      return true;
    } catch {
      createErrorToast(
        t('workspaceGroupPermissions.errors.errorSavingKksRange'),
      );
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  const isRemoving = ref(false);
  async function removeWorkspaceGroupKksRange(): Promise<boolean> {
    try {
      isRemoving.value = true;
      await axios.delete(
        endpointsList.workspaces.removeWorkspaceGroupKksRange(groupId),
      );
      createSuccessToast(
        t('workspaceGroupPermissions.success.kksRangeRemoved'),
      );
      return true;
    } catch {
      createErrorToast(
        t('workspaceGroupPermissions.errors.errorRemovingKksRange'),
      );
      return false;
    } finally {
      isRemoving.value = false;
    }
  }

  return {
    workspaceGroupKksRange,
    fetchWorkspaceGroupKksRange,
    isLoadingWorkspaceGroupKksRange,

    isSavingWorkspaceGroupKksRange: isSaving,
    saveWorkspaceGroupKksRange,

    isRemovingWorkspaceGroupKksRange: isRemoving,
    removeWorkspaceGroupKksRange,
  };
}
